/**
 * Canvas 2D backend. Kept behind its own package so a WebGL backend can be
 * swapped in if large projects need it (see docs/decisions/0002-renderer.md).
 */
import type { Element, Id, Point, Project, Sheet } from '@elec/domain';
import type { PlanLayer, VectorPlan } from '@elec/plan';
import { tr } from '@elec/domain';
import { pageLayout, type PageLayout } from '@elec/layout';
import {
  elementDims,
  elementLabels,
  freeTextSize,
  getSymbol,
  ROOM_TYPES,
  registerProjectSymbols,
  type SymbolDef,
} from '@elec/symbols';
import { circuitLabel, junctionPoints, type SymbolLookup } from './geometry.ts';
import type { Bounds, Scene } from './scene.ts';
import type { Viewport } from './viewport.ts';

export interface Theme {
  desk: string;
  paper: string;
  paperShadow: string;
  grid: string;
  gridMajor: string;
  frame: string;
  frameMuted: string;
  ink: string;
  wire: string;
  selection: string;
  /** Translucent halo behind selected elements. */
  selectionFill: string;
  hover: string;
  tag: string;
  ghost: string;
  snap: string;
  boxWindow: string;
  boxCrossing: string;
}

// Brand palette Signal: the sheet is white paper drawn in graphite, in both modes (as printed and
// inspected); only the desk around it follows the light/dark setting. Selection is hi-vis yellow.
export const LIGHT_THEME: Theme = {
  desk: '#dedbd4',
  paper: '#ffffff',
  paperShadow: 'rgba(0,0,0,0.12)',
  grid: '#f1efea',
  gridMajor: '#e4e1da',
  frame: '#1b1d21',
  frameMuted: '#7a7d83',
  ink: '#1b1d21',
  wire: '#1b1d21',
  selection: '#b07a00',
  selectionFill: 'rgba(255,194,26,0.24)',
  hover: '#e0a400',
  tag: '#3d4046',
  ghost: 'rgba(176,122,0,0.6)',
  snap: '#e0a400',
  boxWindow: '#b07a00',
  boxCrossing: '#2f9e44',
};

export const DARK_THEME: Theme = {
  ...LIGHT_THEME,
  desk: '#131416',
  paperShadow: 'rgba(0,0,0,0.5)',
};

export interface CanvasOverlay {
  ghost?: Element;
  wire?: Point[];
  box?: Bounds & { crossing: boolean };
  snapPoint?: Point;
  snapKind?: 'port' | 'wire' | 'grid';
  ports?: Point[];
  room?: Bounds;
  measure?: [Point, Point];
}

export interface RenderOptions {
  sheet?: Sheet;
  selection?: ReadonlySet<Id>;
  hover?: Id;
  theme?: Theme;
  showGrid?: boolean;
  overlay?: CanvasOverlay;
  /** Decoded sheet backgrounds, by asset id: images, or vector plans imported from DXF. */
  images?: ReadonlyMap<string, CanvasImageSource | VectorPlan>;
  /** Derived labels (cross-references, terminal numbers), by element id. */
  annotations?: ReadonlyMap<Id, ElementAnnotation>;
  /** Revision comparison: items added / changed since the revision, and what was removed. */
  diff?: RenderDiff;
}

export interface RenderDiff {
  added: ReadonlySet<Id>;
  changed: ReadonlySet<Id>;
  /** Removed elements (from the revision) on this sheet. */
  removedElements: Element[];
  /** Removed wires on this sheet, as polylines. */
  removedWires: Point[][];
}

export const DIFF_COLORS = { added: '#2f9e44', changed: '#e8590c', removed: '#e03131' };

export interface ElementAnnotation {
  extra: string[];
  ports: Array<{ x: number; y: number; text: string; anchor: 'start' | 'end' | 'middle' }>;
}

export interface RenderStats {
  drawnElements: number;
  drawnWires: number;
  ms: number;
}

interface CachedSymbol {
  stroke: Path2D;
  dashed: Path2D | undefined;
  fill: Path2D | undefined;
}

const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export class Canvas2DRenderer {
  // Keyed by definition object: a user symbol edited in place gets a new object, hence new paths.
  private cache = new WeakMap<SymbolDef, CachedSymbol>();
  private layoutCache = new WeakMap<Project, Map<string, PageLayout>>();
  private readonly symbols: SymbolLookup;
  /** Language for room type names drawn on plans. */
  locale: 'fr' | 'en' | 'nl' = 'fr';
  private project: Project | undefined;
  private paperColor = '#ffffff';

  constructor(symbols: SymbolLookup = getSymbol) {
    this.symbols = symbols;
  }

  private symbolPaths(def: SymbolDef): CachedSymbol {
    let c = this.cache.get(def);
    if (!c) {
      const join = (ds: string[] | undefined) => {
        if (!ds?.length) return undefined;
        const p = new Path2D();
        for (const d of ds) p.addPath(new Path2D(d));
        return p;
      };
      c = { stroke: join(def.paths) ?? new Path2D(), dashed: join(def.dashed), fill: join(def.fills) };
      this.cache.set(def, c);
    }
    return c;
  }

  private layout(project: Project, sheet: Sheet): PageLayout {
    let m = this.layoutCache.get(project);
    if (!m) this.layoutCache.set(project, (m = new Map()));
    let l = m.get(sheet.id);
    if (!l) m.set(sheet.id, (l = pageLayout(project, sheet)));
    return l;
  }

  render(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    dpr: number,
    vp: Viewport,
    scene: Scene,
    project: Project,
    opts: RenderOptions = {},
  ): RenderStats {
    const t0 = performance.now();
    registerProjectSymbols(project.symbols);
    this.project = project;
    const theme = opts.theme ?? LIGHT_THEME;
    this.paperColor = theme.paper;
    const s = vp.scale;
    const X = (x: number) => x * s + vp.offsetX;
    const Y = (y: number) => y * s + vp.offsetY;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = theme.desk;
    ctx.fillRect(0, 0, width, height);

    const sheet = opts.sheet ?? project.sheets[scene.sheetId];
    const page = sheet ? this.layout(project, sheet) : undefined;
    if (page) {
      ctx.fillStyle = theme.paperShadow;
      ctx.fillRect(X(0) + 3, Y(0) + 3, page.width * s, page.height * s);
      ctx.fillStyle = theme.paper;
      ctx.fillRect(X(0), Y(0), page.width * s, page.height * s);
    } else {
      ctx.fillStyle = theme.paper;
      ctx.fillRect(0, 0, width, height);
    }
    if (opts.showGrid !== false && page) this.drawGrid(ctx, width, height, vp, theme, page);
    const bg = sheet?.background;
    const img = bg && opts.images?.get(bg.assetId);
    if (bg && img && page) {
      // Clip to the paper: a plan larger than the sheet must not spill onto the desk.
      ctx.save();
      ctx.beginPath();
      ctx.rect(X(0), Y(0), page.width * s, page.height * s);
      ctx.clip();
      ctx.globalAlpha = bg.opacity;
      if (isVectorPlan(img)) drawVectorPlan(ctx, img, X(bg.x), Y(bg.y), bg.width * s, bg.height * s, s);
      else ctx.drawImage(img, X(bg.x), Y(bg.y), bg.width * s, bg.height * s);
      ctx.restore();
    }
    if (page) this.drawPage(ctx, vp, page, theme);

    const vb = vp.visibleBounds(width, height);
    // Labels stick out of symbol bounds: query a little wider.
    const visible = scene.query({
      minX: vb.minX - 25,
      minY: vb.minY - 15,
      maxX: vb.maxX + 5,
      maxY: vb.maxY + 5,
    });
    const lineW = Math.max(0.25 * s, 1); // 0.25 mm pen, never thinner than 1 px
    let drawnElements = 0;
    let drawnWires = 0;

    // ---- wires: one batched path, highlighted ones on top
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    const highlighted: typeof visible = [];
    ctx.beginPath();
    for (const it of visible) {
      if (it.kind !== 'wire' || !it.points) continue;
      if (
        opts.selection?.has(it.id) ||
        opts.hover === it.id ||
        opts.diff?.added.has(it.id) ||
        opts.diff?.changed.has(it.id)
      ) {
        highlighted.push(it);
        continue;
      }
      const [first, ...rest] = it.points;
      ctx.moveTo(X(first!.x), Y(first!.y));
      for (const q of rest) ctx.lineTo(X(q.x), Y(q.y));
      drawnWires++;
    }
    ctx.strokeStyle = theme.wire;
    ctx.lineWidth = lineW;
    ctx.stroke();
    for (const it of highlighted) {
      ctx.beginPath();
      const [first, ...rest] = it.points!;
      ctx.moveTo(X(first!.x), Y(first!.y));
      for (const q of rest) ctx.lineTo(X(q.x), Y(q.y));
      const diffColor = opts.diff?.added.has(it.id)
        ? DIFF_COLORS.added
        : opts.diff?.changed.has(it.id)
          ? DIFF_COLORS.changed
          : undefined;
      ctx.strokeStyle = opts.selection?.has(it.id)
        ? theme.selection
        : opts.hover === it.id || !diffColor
          ? theme.hover
          : diffColor;
      ctx.lineWidth = lineW * 2;
      ctx.stroke();
      drawnWires++;
    }
    // Wire labels (cable references) at the middle of the longest segment.
    if (s >= 2) {
      ctx.fillStyle = theme.tag;
      ctx.font = `${2.2 * s}px ${FONT}`;
      for (const it of visible) {
        if (it.kind !== 'wire' || !it.points) continue;
        const label = project.wires[it.id]?.label;
        if (label) this.drawWireLabel(ctx, it.points, label, X, Y, s);
      }
    }

    // ---- junction dots
    const dotR = Math.max(0.7 * s, 2);
    ctx.fillStyle = theme.wire;
    ctx.beginPath();
    for (const p of junctionPoints(project, scene.sheetId, this.symbols)) {
      if (p.x < vb.minX - 1 || p.x > vb.maxX + 1 || p.y < vb.minY - 1 || p.y > vb.maxY + 1) continue;
      ctx.moveTo(X(p.x) + dotR, Y(p.y));
      ctx.arc(X(p.x), Y(p.y), dotR, 0, Math.PI * 2);
    }
    ctx.fill();

    // ---- elements
    const showLabels = s >= 1.8;
    const lod = s < 0.6; // a symbol under ~6 px: draw a box instead
    if (lod) ctx.beginPath();
    for (const it of visible) {
      if (it.kind !== 'element') continue;
      const el = project.elements[it.id];
      if (!el) continue;
      drawnElements++;
      if (lod) {
        ctx.rect(X(it.minX), Y(it.minY), (it.maxX - it.minX) * s, (it.maxY - it.minY) * s);
        continue;
      }
      const def = this.symbols(el.symbolId);
      if (!def) continue;
      const sel = opts.selection?.has(el.id);
      const diffColor = opts.diff?.added.has(el.id)
        ? DIFF_COLORS.added
        : opts.diff?.changed.has(el.id)
          ? DIFF_COLORS.changed
          : undefined;
      const color = sel ? theme.selection : opts.hover === el.id ? theme.hover : (diffColor ?? theme.ink);
      this.drawElement(ctx, el, def, vp, dpr, color, lineW);
      if (showLabels) {
        const ann = opts.annotations?.get(el.id);
        this.drawLabels(ctx, el, def, X, Y, s, sel ? theme.selection : theme.tag, ann?.extra);
        if (ann?.ports.length && s >= 2.5) {
          ctx.fillStyle = theme.tag;
          ctx.font = `${1.8 * s}px ${FONT}`;
          for (const pl of ann.ports) {
            ctx.textAlign = pl.anchor === 'middle' ? 'center' : pl.anchor === 'end' ? 'right' : 'left';
            ctx.fillText(pl.text, X(pl.x), Y(pl.y));
          }
        }
      }
    }
    if (lod) {
      ctx.strokeStyle = theme.ink;
      ctx.lineWidth = 1;
      ctx.stroke();
    }
    // Selection outlines
    if (opts.selection?.size && !lod) {
      ctx.fillStyle = theme.selectionFill;
      for (const it of visible) {
        if (it.kind === 'element' && opts.selection.has(it.id))
          ctx.fillRect(
            X(it.minX) - 3,
            Y(it.minY) - 3,
            (it.maxX - it.minX) * s + 6,
            (it.maxY - it.minY) * s + 6,
          );
      }
      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = theme.selection;
      ctx.lineWidth = 1;
      for (const it of visible) {
        if (it.kind === 'element' && opts.selection.has(it.id))
          ctx.strokeRect(
            X(it.minX) - 3,
            Y(it.minY) - 3,
            (it.maxX - it.minX) * s + 6,
            (it.maxY - it.minY) * s + 6,
          );
      }
      ctx.setLineDash([]);
    }

    // Revision comparison: what was removed, drawn dashed in red.
    if (opts.diff && !lod) {
      ctx.globalAlpha = 0.75;
      for (const el of opts.diff.removedElements) {
        const def = this.symbols(el.symbolId);
        if (def) this.drawElement(ctx, el, def, vp, dpr, DIFF_COLORS.removed, lineW);
      }
      ctx.setLineDash([4, 3]);
      ctx.strokeStyle = DIFF_COLORS.removed;
      ctx.lineWidth = lineW * 1.5;
      ctx.beginPath();
      for (const pts of opts.diff.removedWires) {
        if (pts.length < 2) continue;
        ctx.moveTo(X(pts[0]!.x), Y(pts[0]!.y));
        for (const q of pts.slice(1)) ctx.lineTo(X(q.x), Y(q.y));
      }
      ctx.stroke();
      ctx.setLineDash([]);
      ctx.globalAlpha = 1;
    }
    if (opts.overlay) this.drawOverlay(ctx, vp, dpr, opts.overlay, theme, lineW);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    return { drawnElements, drawnWires, ms: performance.now() - t0 };
  }

  private drawElement(
    ctx: CanvasRenderingContext2D,
    el: Element,
    def: SymbolDef,
    vp: Viewport,
    dpr: number,
    color: string,
    lineW: number,
  ) {
    const s = vp.scale;
    const dims = elementDims(def, el);
    const cx = dims.width / 2;
    const cy = dims.height / 2;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.translate((el.x + cx) * s + vp.offsetX, (el.y + cy) * s + vp.offsetY);
    ctx.rotate((el.rotation * Math.PI) / 180);
    ctx.scale(el.mirrored ? -s : s, s);
    ctx.translate(-cx, -cy);
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.lineWidth = lineW / s;
    if (def.box) {
      const rail = el.props.style === 'rail';
      if (rail) {
        ctx.globalAlpha = 0.15;
        ctx.fillRect(0, 0, dims.width, dims.height);
        ctx.globalAlpha = 1;
      } else if (el.props.style === 'spare') ctx.setLineDash([1.5, 1]);
      else {
        // Device front: hides the rail behind it.
        ctx.fillStyle = this.paperColor;
        ctx.fillRect(0, 0, dims.width, dims.height);
        ctx.fillStyle = color;
      }
      ctx.strokeRect(0, 0, dims.width, dims.height);
      ctx.setLineDash([]);
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      const lines = String(el.props.lines ?? '').split('\n');
      lines.forEach((l, i) => {
        ctx.font = `${i === 0 ? '600 ' : ''}${i === 0 ? 3 : 2.2}px ${FONT}`;
        ctx.fillText(l, dims.width / 2, 6 + i * 3.6, dims.width - 1);
      });
    } else if (def.room) {
      ctx.setLineDash([2, 1.2]);
      ctx.strokeRect(0, 0, dims.width, dims.height);
      ctx.setLineDash([]);
      const type = ROOM_TYPES.find((r) => r.value === el.props.roomType);
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.font = `600 3px ${FONT}`;
      ctx.fillText(String(el.props.name ?? ''), 2, 5);
      const label = type?.label[this.locale] ?? '';
      if (
        type &&
        type.value !== 'other' &&
        label.toLowerCase() !== String(el.props.name ?? '').toLowerCase()
      ) {
        ctx.font = `2.2px ${FONT}`;
        ctx.fillText(
          type.label[this.locale] + (type.wet ? tr(this.locale, ' · humide', ' · wet', ' · vochtig') : ''),
          2,
          8.2,
        );
      }
    } else if (def.freeText) {
      const { size } = freeTextSize(el.props);
      ctx.font = `${size}px ${FONT}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      String(el.props.text ?? '')
        .split('\n')
        .forEach((line, i) => ctx.fillText(line, 0, size * 0.85 + i * size * 1.25));
    } else {
      const paths = this.symbolPaths(def);
      ctx.stroke(paths.stroke);
      if (paths.dashed) {
        ctx.setLineDash([1, 0.8]);
        ctx.stroke(paths.dashed);
        ctx.setLineDash([]);
      }
      if (paths.fill) ctx.fill(paths.fill);
      if (def.texts) {
        ctx.textAlign = 'center';
        ctx.textBaseline = 'alphabetic';
        for (const t of def.texts) {
          // Keep symbol text upright when the symbol is mirrored.
          ctx.save();
          if (el.mirrored) {
            ctx.translate(t.x, 0);
            ctx.scale(-1, 1);
            ctx.translate(-t.x, 0);
          }
          ctx.font = `${t.size}px ${FONT}`;
          ctx.fillText(t.text, t.x, t.y);
          ctx.restore();
        }
      }
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private drawLabels(
    ctx: CanvasRenderingContext2D,
    el: Element,
    def: SymbolDef,
    X: (x: number) => number,
    Y: (y: number) => number,
    s: number,
    color: string,
    extra: string[] = [],
  ) {
    const lines = elementLabels(def, el, [...(this.project ? circuitLabel(this.project, el) : []), ...extra]);
    if (!lines.length) return;
    ctx.fillStyle = color;
    ctx.textBaseline = 'alphabetic';
    for (const l of lines) {
      ctx.font = `${l.bold ? '600 ' : ''}${l.size * s}px ${FONT}`;
      ctx.textAlign = l.anchor === 'middle' ? 'center' : 'left';
      ctx.fillText(l.text, X(l.x), Y(l.y));
    }
  }

  private drawWireLabel(
    ctx: CanvasRenderingContext2D,
    pts: Point[],
    label: string,
    X: (x: number) => number,
    Y: (y: number) => number,
    s: number,
  ) {
    let best = 0;
    let bi = 0;
    for (let i = 0; i < pts.length - 1; i++) {
      const d = Math.hypot(pts[i + 1]!.x - pts[i]!.x, pts[i + 1]!.y - pts[i]!.y);
      if (d > best) {
        best = d;
        bi = i;
      }
    }
    const a = pts[bi]!;
    const b = pts[bi + 1]!;
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;
    const vertical = Math.abs(a.x - b.x) < Math.abs(a.y - b.y);
    ctx.save();
    if (vertical) {
      ctx.translate(X(mx) - 1.2 * s, Y(my));
      ctx.rotate(-Math.PI / 2);
      ctx.textAlign = 'center';
      ctx.fillText(label, 0, 0);
    } else {
      ctx.textAlign = 'center';
      ctx.fillText(label, X(mx), Y(my) - 1.2 * s);
    }
    ctx.restore();
  }

  private drawPage(ctx: CanvasRenderingContext2D, vp: Viewport, page: PageLayout, theme: Theme) {
    const s = vp.scale;
    const X = (x: number) => x * s + vp.offsetX;
    const Y = (y: number) => y * s + vp.offsetY;
    ctx.lineCap = 'butt';
    ctx.strokeStyle = theme.frame;
    const byWidth = new Map<number, typeof page.lines>();
    for (const l of page.lines) {
      const list = byWidth.get(l.width) ?? [];
      list.push(l);
      byWidth.set(l.width, list);
    }
    for (const [w, list] of byWidth) {
      ctx.lineWidth = Math.max(w * s, 1);
      ctx.beginPath();
      for (const l of list) {
        ctx.moveTo(X(l.x1), Y(l.y1));
        ctx.lineTo(X(l.x2), Y(l.y2));
      }
      ctx.stroke();
    }
    if (s < 0.9) return; // title block text unreadable at this zoom
    ctx.textBaseline = 'alphabetic';
    for (const t of page.texts) {
      ctx.fillStyle = t.muted ? theme.frameMuted : theme.frame;
      ctx.font = `${t.bold ? '600 ' : ''}${t.size * s}px ${FONT}`;
      ctx.textAlign = t.anchor === 'middle' ? 'center' : t.anchor === 'end' ? 'right' : 'left';
      ctx.fillText(t.text, X(t.x), Y(t.y));
    }
  }

  private drawOverlay(
    ctx: CanvasRenderingContext2D,
    vp: Viewport,
    dpr: number,
    o: CanvasOverlay,
    theme: Theme,
    lineW: number,
  ) {
    const s = vp.scale;
    const X = (x: number) => x * s + vp.offsetX;
    const Y = (y: number) => y * s + vp.offsetY;
    if (o.ghost) {
      const def = this.symbols(o.ghost.symbolId);
      if (def) {
        this.drawElement(ctx, o.ghost, def, vp, dpr, theme.ghost, lineW);
        this.drawLabels(ctx, o.ghost, def, X, Y, s, theme.ghost);
      }
    }
    if (o.ports?.length) {
      ctx.strokeStyle = theme.snap;
      ctx.lineWidth = 1;
      const r = Math.max(0.8 * s, 2.5);
      for (const p of o.ports) {
        ctx.beginPath();
        ctx.arc(X(p.x), Y(p.y), r, 0, Math.PI * 2);
        ctx.stroke();
      }
    }
    if (o.wire && o.wire.length >= 2) {
      ctx.beginPath();
      ctx.moveTo(X(o.wire[0]!.x), Y(o.wire[0]!.y));
      for (const q of o.wire.slice(1)) ctx.lineTo(X(q.x), Y(q.y));
      ctx.strokeStyle = theme.ghost;
      ctx.lineWidth = lineW * 1.5;
      ctx.stroke();
    }
    if (o.snapPoint) {
      const { x, y } = o.snapPoint;
      ctx.strokeStyle = theme.snap;
      ctx.fillStyle = theme.snap;
      ctx.lineWidth = 1.5;
      const r = Math.max(1.2 * s, 4);
      ctx.beginPath();
      if (o.snapKind === 'port') ctx.arc(X(x), Y(y), r, 0, Math.PI * 2);
      else if (o.snapKind === 'wire') ctx.rect(X(x) - r, Y(y) - r, 2 * r, 2 * r);
      else {
        ctx.moveTo(X(x) - r, Y(y));
        ctx.lineTo(X(x) + r, Y(y));
        ctx.moveTo(X(x), Y(y) - r);
        ctx.lineTo(X(x), Y(y) + r);
      }
      ctx.stroke();
    }
    if (o.room) {
      const r = o.room;
      ctx.strokeStyle = theme.snap;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.strokeRect(X(r.minX), Y(r.minY), (r.maxX - r.minX) * s, (r.maxY - r.minY) * s);
      ctx.setLineDash([]);
      ctx.fillStyle = theme.snap;
      ctx.font = `12px ${FONT}`;
      ctx.textAlign = 'left';
      ctx.fillText(
        `${(r.maxX - r.minX).toFixed(1)} × ${(r.maxY - r.minY).toFixed(1)} mm`,
        X(r.minX),
        Y(r.minY) - 6,
      );
    }
    if (o.measure) {
      const [a, b] = o.measure;
      ctx.strokeStyle = theme.snap;
      ctx.fillStyle = theme.snap;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(X(a.x), Y(a.y));
      ctx.lineTo(X(b.x), Y(b.y));
      ctx.stroke();
      for (const q of [a, b]) {
        ctx.beginPath();
        ctx.arc(X(q.x), Y(q.y), 4, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.font = `12px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.fillText(
        `${Math.hypot(b.x - a.x, b.y - a.y).toFixed(1)} mm`,
        (X(a.x) + X(b.x)) / 2,
        (Y(a.y) + Y(b.y)) / 2 - 8,
      );
    }
    if (o.box) {
      const b = o.box;
      const color = b.crossing ? theme.boxCrossing : theme.boxWindow;
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.08;
      ctx.fillRect(X(b.minX), Y(b.minY), (b.maxX - b.minX) * s, (b.maxY - b.minY) * s);
      ctx.globalAlpha = 1;
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      if (b.crossing) ctx.setLineDash([5, 4]);
      ctx.strokeRect(X(b.minX) + 0.5, Y(b.minY) + 0.5, (b.maxX - b.minX) * s, (b.maxY - b.minY) * s);
      ctx.setLineDash([]);
    }
  }

  private drawGrid(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    vp: Viewport,
    theme: Theme,
    page: PageLayout,
  ) {
    // Pick a grid step (mm) that stays at least 8 px apart on screen; only on the paper.
    const steps = [2.5, 5, 10, 25, 50, 100];
    const step = steps.find((st) => st * vp.scale >= 8);
    if (!step) return;
    const major = step * 4;
    const v = vp.visibleBounds(width, height);
    const b = {
      minX: Math.max(v.minX, 0),
      minY: Math.max(v.minY, 0),
      maxX: Math.min(v.maxX, page.width),
      maxY: Math.min(v.maxY, page.height),
    };
    if (b.minX >= b.maxX || b.minY >= b.maxY) return;
    const x0 = Math.round(b.minX * vp.scale + vp.offsetX);
    const x1 = Math.round(b.maxX * vp.scale + vp.offsetX);
    const y0 = Math.round(b.minY * vp.scale + vp.offsetY);
    const y1 = Math.round(b.maxY * vp.scale + vp.offsetY);
    const lines = (st: number) => {
      ctx.beginPath();
      for (let x = Math.ceil(b.minX / st) * st; x <= b.maxX; x += st) {
        const sx = Math.round(x * vp.scale + vp.offsetX) + 0.5;
        ctx.moveTo(sx, y0);
        ctx.lineTo(sx, y1);
      }
      for (let y = Math.ceil(b.minY / st) * st; y <= b.maxY; y += st) {
        const sy = Math.round(y * vp.scale + vp.offsetY) + 0.5;
        ctx.moveTo(x0, sy);
        ctx.lineTo(x1, sy);
      }
      ctx.stroke();
    };
    ctx.lineWidth = 1;
    ctx.strokeStyle = theme.grid;
    lines(step);
    ctx.strokeStyle = theme.gridMajor;
    lines(major);
  }
}

const isVectorPlan = (v: unknown): v is VectorPlan =>
  !!v && typeof v === 'object' && (v as VectorPlan).v === 1 && Array.isArray((v as VectorPlan).layers);

/** Path2D of each layer in plan coordinates, built once per plan. */
const planPaths = new WeakMap<PlanLayer, Path2D>();
function layerPath2D(l: PlanLayer): Path2D {
  let p = planPaths.get(l);
  if (!p) {
    p = new Path2D();
    for (const pl of l.lines) {
      if (pl.length < 4) continue;
      p.moveTo(pl[0]!, pl[1]!);
      for (let i = 2; i < pl.length; i += 2) p.lineTo(pl[i]!, pl[i + 1]!);
    }
    planPaths.set(l, p);
  }
  return p;
}

/** A DXF floor plan behind a position plan: thin grey lines and texts, scaled into (x, y, w, h) px. */
function drawVectorPlan(
  ctx: CanvasRenderingContext2D,
  plan: VectorPlan,
  x: number,
  y: number,
  w: number,
  h: number,
  pxPerMm: number,
) {
  const [minX, minY, maxX, maxY] = plan.bounds;
  const kx = w / (maxX - minX || 1);
  const ky = h / (maxY - minY || 1);
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(kx, -ky);
  ctx.translate(-minX, -maxY);
  // 0.13 mm on paper, at least 0.6 px on screen, whatever the zoom.
  ctx.lineWidth = Math.max(0.13 * pxPerMm, 0.6) / kx;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = PLAN_INK;
  if (typeof Path2D !== 'undefined') for (const l of plan.layers) ctx.stroke(layerPath2D(l));
  ctx.restore();
  ctx.save();
  ctx.fillStyle = PLAN_INK;
  for (const l of plan.layers)
    for (const t of l.texts) {
      const size = t.h * ky;
      if (size < 3) continue; // unreadable at this zoom
      ctx.save();
      ctx.translate(x + (t.x - minX) * kx, y + (maxY - t.y) * ky);
      ctx.rotate((-t.rot * Math.PI) / 180);
      ctx.font = `${size}px sans-serif`;
      ctx.textAlign = t.align === 'middle' ? 'center' : t.align === 'end' ? 'right' : 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(t.text, 0, 0);
      ctx.restore();
    }
  ctx.restore();
}

/** Floor plans are drawn in grey, under the electrical points (same on screen and in print). */
const PLAN_INK = '#7a7d82';
